import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    isWidget,
    getBorderColourClassName,
    Sizes,
    Colours,
    CardBanner,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
    Checkbox,
    Config,
} from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import FOCUS_VARIANTS from '../definitions/FocusVariants';
import CreditCardShape from '../definitions/CreditCardShape';
import HIDE_COMPARISON_IF_MOBILE from '../styles/CardComparisonSupportStyles';
import CARD_ID_EXCEPTIONS from '../definitions/CardIdExceptions';
import isCIBCCreditCard from '../functions/isCIBCCreditCard';
import getCardBannerMessage from '../functions/getCardBannerMessage';
import extractRhAnalyticsFromCreditCard from '../functions/extractRhAnalyticsFromCreditCard';
import CreditCardCTA from './CreditCardCTA';
import CardLayoutFooter from './CardLayoutFooter';
import CardLayoutTitleArea from './CardLayoutTitleArea';
import CardLayoutHighlights from './CardLayoutHighlights';
import ApprovalLikelihoodPill from './ApprovalLikelihoodPill';


function CreditCardLayout({
    cardInfo,
    productType,

    onClick,
    hasEligibilityCTA = false,
    borderColour,

    hasCreditScore = false,
    hasCategoryPill = false,
    hasBestUsedForPill = false,
    isCardFinder = false,
    isShowingApprovalLikelihood = false,

    focusVariant,
    isEqualizeHeight = false,

    isCardSelected,
    onCardComparisonChange,
    isComparisonCheckboxDisabled,
}) {
    const {
        giftCardOffer,
        hasReferralOffer,
        referralOfferValue,
        cardImageURL,
        name,
        cardTypes,
        id: cardId,
        ratehubReview,
        isSponsored,
        isMonetized,
        displayType,
        bestUsedFor,
        approvalLikelihood,
        minimumCreditScore,
        prohibitedProvinces,
    } = cardInfo;

    const intl = useIntl();

    // minimumCreditScore will be one of [ 300, 560, 660, 725, 760, null ]
    const showCreditScoreMessage = hasCreditScore && minimumCreditScore;

    const isAWidget = isWidget();
    const colourOfBorder = borderColour || Colours.STONE;
    const isCIBC = isCIBCCreditCard(cardInfo);
    const rightColumnWidth = getCardWidth({ isWide: isCardFinder });
    const cardBannerMessage = getCardBannerMessage({ giftCardOffer, hasReferralOffer, referralOfferValue }, intl);

    const enableCompareCheckbox = Config.ENABLE_CC_COMPARISON_DOCUMENT && onCardComparisonChange;

    return (
        <Container
            className={classNames('rh-box-sizing-border-box rh-bg-coconut rh-border-style-solid rh-border-width-1px',
                getBorderColourClassName(colourOfBorder),
                ANALYTICS_ATTRIBUTE_NAME,
            )}
            ref={self => self && (self[ANALYTICS_KEY] = extractRhAnalyticsFromCreditCard(cardInfo))}
            borderRadius={BORDER_RADIUS}
            rightColumnWidth={rightColumnWidth}
            data-test-name={'credit-card'}
            data-test-product-type={productType}
        >
            <Choose>
                <When condition={cardBannerMessage}>
                    <div className="rh-box-sizing-border-box">
                        <CardBanner text={cardBannerMessage} />
                    </div>
                </When>
                <Otherwise>
                    {/* there is no banner, but we might fill that space with a CardBanner placeholder */}
                    <If condition={isEqualizeHeight}>
                        <span className="rh-display-inline-block rh-icon-xs rh-my-0_25">
                            {' '}
                        </span>
                    </If>
                </Otherwise>
            </Choose>

            <div className={classNames(
                'inner-body rh-display-flex rh-flex-wrap rh-px-0_5 rh-mb-1 rh-gap-0_5 rh-box-sizing-border-box'
            )}
            >
                <div
                    className={classNames('row1 rh-display-flex rh-flex-wrap rh-box-sizing-border-box', {
                        'rh-mt-1': !isEqualizeHeight,
                        'rh-mt-0_25': isEqualizeHeight && giftCardOffer,
                    })}
                >
                    <div className="title-area rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutTitleArea
                            name={name}
                            ratehubReview={ratehubReview}
                            displayType={
                                hasCategoryPill && !CARD_ID_EXCEPTIONS.CapitalOneGuaranteed.includes(cardInfo.id)
                                    ? displayType
                                    : undefined}
                            bestUsedFor={hasBestUsedForPill
                                ? bestUsedFor
                                : undefined}
                            isSponsored={isSponsored && !isCardFinder}
                            isMonetized={isMonetized}

                            onClick={onClick}
                            isAWidget={isAWidget}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="card-image-wrapper rh-mx-0_25 rh-box-sizing-border-box">
                        <button
                            className="card-button rh-display-block rh-p-0 rh-m-auto rh-border-width-0"
                            data-name="card-image"
                            onClick={onClick}
                            type="button"
                        >
                            <img
                                src={cardImageURL}
                                alt={name}
                                width={rightColumnWidth}
                                height={getCardHeight({ isWide: isCardFinder })}
                            />
                        </button>
                    </div>
                </div>

                <div className="row2 rh-display-flex rh-flex-wrap-reverse rh-box-sizing-border-box">
                    <div className="body rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutHighlights
                            cardInfo={cardInfo}
                            productType={productType}
                            focusVariant={focusVariant}
                            isCIBC={isCIBC}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="cta-wrapper rh-mx-0_25 rh-mb-1_5 rh-box-sizing-border-box">
                        <If condition={showCreditScoreMessage}>
                            <p
                                className={classNames(
                                    'rh-text-xs rh-mt-0 rh-mb-0_5 rh-fg-blackberry-light',
                                    'weight-medium rh-text-align-center'
                                )}
                                data-test-name="minimum-credit-score-cta"
                            >
                                <FormattedMessage
                                    id="edb.cc.layout.minimum-credit-score"
                                    defaultMessage="Minimum credit score: <score>{minimumCreditScore}</score>"
                                    values={{
                                        minimumCreditScore,
                                        // eslint-disable-next-line react/jsx-one-expression-per-line
                                        score: str => <span className="rh-text-s weight-bold">{str}</span>,
                                    }}
                                />
                            </p>
                        </If>

                        <If condition={isShowingApprovalLikelihood && approvalLikelihood}>
                            <ApprovalLikelihoodPill
                                className="approval-likelihood-pill rh-mx-auto rh-mb-0_5 rh-box-sizing-border-box"
                                approvalLikelihood={approvalLikelihood}
                                isCompact
                            />
                        </If>

                        <CreditCardCTA
                            cardId={cardId}
                            giftCardOffer={giftCardOffer}
                            hasReferralOffer={hasReferralOffer}
                            isMonetized={isMonetized}
                            prohibitedProvinces={prohibitedProvinces}

                            cardTypes={cardTypes}
                            buttonSize="small"
                            hasEligibilityCTA={hasEligibilityCTA}
                            onClick={onClick}
                        />

                        <If condition={enableCompareCheckbox}>
                            <div
                                className={classNames('rh-text-align-center',
                                    { 'should-hide-checkbox-on-mobile': !Config.FORCE_CC_COMPARISON_ON_MOBILE }
                                )}
                            >
                                <Checkbox
                                    id={cardId}
                                    name={cardId}
                                    value={isCardSelected}
                                    onChange={onCardComparisonChange}
                                    isDisabled={isComparisonCheckboxDisabled}
                                    className="rh-mt-0_75"
                                    label={(
                                        <FormattedMessage
                                            id="edb.cc.layout.add-compare"
                                            defaultMessage="Add to compare"
                                        />
                                    )}
                                    data-name="credit-card-add-to-compare"
                                />
                            </div>
                        </If>
                    </div>
                </div>
            </div>

            <div
                className="rh-flex-grow-1 rh-flex-shrink-1 rh-width-100p rh-box-sizing-border-box"
                data-test-name="card-footer"
            >
                <CardLayoutFooter
                    cardInfo={cardInfo}
                    borderRadius={BORDER_RADIUS}
                    isAWidget={isAWidget}
                    isCIBC={isCIBC}
                />
            </div>
        </Container>
    );
}

CreditCardLayout.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)).isRequired,

    onClick: PropTypes.func,
    borderColour: PropTypes.oneOf(Object.values(Colours)),

    hasCreditScore: PropTypes.bool,
    hasEligibilityCTA: PropTypes.bool,
    hasCategoryPill: PropTypes.bool,
    hasBestUsedForPill: PropTypes.bool,
    isCardFinder: PropTypes.bool,
    isShowingApprovalLikelihood: PropTypes.bool,

    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
    isEqualizeHeight: PropTypes.bool,

    isComparisonCheckboxDisabled: PropTypes.bool,
    isCardSelected: PropTypes.bool,
    onCardComparisonChange: PropTypes.func,
};


// we need a wider second column to accommodate approval likelihood message (CardFinder only) and longer FR CTA
// increasing this width means long card names are more likely to wrap (noting that some card names will always wrap)
function getCardWidth({ isWide = false } = {}) {
    return isWide
        ? '192px'
        : '182px';
}

function getCardHeight({ isWide = false } = {}) {
    return parseInt(getCardWidth({ isWide })) * 0.625 + 'px';
}

const BORDER_RADIUS = '4px';


const Container = styled.div`
    ${({ borderRadius }) => `border-radius: ${borderRadius}`};

    box-shadow: 1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 1px 2px 1px ${Colours.STONE_LIGHT},
        -1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 -1px 2px 1px ${Colours.STONE_LIGHT};

    > .inner-body {
        > .row1,
        > .row2 {
            flex: 1 1 100%;
            gap: 0 ${Sizes.SPACING.QUARTER};
        }

        > .row1 > .title-area,
        > .row2 > .body {
            flex: 30 1 400px; // greedy title-area and body widths
        }

        > .row1 > .card-image-wrapper,
        > .row2 > .cta-wrapper {
            flex: 1 0 ${({ rightColumnWidth }) => `${rightColumnWidth}`};
        }

        > .row2 {
            > .cta-wrapper {
                > .approval-likelihood-pill {
                    max-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                }
                
                .should-hide-checkbox-on-mobile {
                    ${HIDE_COMPARISON_IF_MOBILE}
                }
            }
        }
    }

`;

export default observer(CreditCardLayout);
